import { Directive, ElementRef, forwardRef, HostListener, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { normalizeDecimalTyping, parseDecimalInput } from '@core/utils/number-utils';

/**
 * Lets a numeric field accept a decimal comma or a decimal point.
 * The form value stays a number.
 */
@Directive({
    selector: 'input[type=number], input[decimalInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DecimalInputDirective),
            multi: true,
        },
    ],
})
export class DecimalInputDirective implements ControlValueAccessor {
    private el = inject(ElementRef<HTMLInputElement>);
    private onChange: (value: number | null) => void = () => {};
    private onTouched: () => void = () => {};
    private lastEmitted: number | null = null;

    constructor() {
        const input = this.el.nativeElement;
        input.dataset['decimalInput'] = 'true';
        input.inputMode = 'decimal';
        input.type = 'text';
    }

    writeValue(value: unknown): void {
        const input = this.el.nativeElement;
        if (document.activeElement === input) {
            return;
        }

        if (value === null || value === undefined || value === '') {
            this.lastEmitted = null;
            input.value = '';
            return;
        }

        const parsed = parseDecimalInput(value);
        this.lastEmitted = parsed;
        input.value = parsed === null ? '' : String(parsed);
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.el.nativeElement.disabled = isDisabled;
    }

    @HostListener('input')
    onInput(): void {
        const input = this.el.nativeElement;
        const normalized = normalizeDecimalTyping(input.value);
        if (normalized !== input.value) {
            const start = input.selectionStart ?? input.value.length;
            const delta = normalized.length - input.value.length;
            input.value = normalized;
            const pos = Math.max(0, Math.min(normalized.length, start + delta));
            input.setSelectionRange(pos, pos);
        }

        this.emit(parseDecimalInput(input.value));
    }

    @HostListener('blur')
    onBlur(): void {
        const input = this.el.nativeElement;
        const parsed = parseDecimalInput(input.value);
        input.value = parsed === null ? '' : String(parsed);
        this.emit(parsed);
        this.onTouched();
    }

    private emit(value: number | null): void {
        if (Object.is(this.lastEmitted, value)) {
            return;
        }

        this.lastEmitted = value;
        this.onChange(value);
    }
}
